import { useEffect, useRef, useState } from "react";

/**
 * True once the element has been scrolled near, and true forever after.
 *
 * Written for the spectral date strips, where every preview card costs one
 * Sentinel Hub request against a capped monthly quota: rendering a page of 20
 * eagerly spent 20 requests before the user had shown interest in any single
 * date, and 82% of the previews bought that way were never opened.
 *
 * Pass `root` when the element is clipped by a scroll container rather than by
 * the viewport — a horizontal strip is exactly that case, and a viewport-rooted
 * margin cannot see past the container's edge, so nothing would ever preload
 * ahead of the scroll.
 */
export function useInView<T extends HTMLElement>(options?: {
  root?: React.RefObject<HTMLElement> | null;
  /** How far outside the root to start loading. Default: three cards ahead. */
  rootMargin?: string;
}): { ref: React.RefObject<T>; inView: boolean } {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  const root = options?.root;
  const rootMargin = options?.rootMargin ?? "0px 200px";

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    // No IntersectionObserver (older WebViews): load rather than show nothing.
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { root: root?.current ?? null, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, root, rootMargin]);

  return { ref, inView };
}
